import {
  ReactNode,
  createContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { IExtraContent, IExtraContents } from '@dtos/Course'
import { useToastController } from '@tamagui/toast'
import { useTranslation } from 'react-i18next'
import { useGetExtraContents } from '@services/api/graphql/extraContents'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface IExtraContentsProviderProps {
  extraContents?: IExtraContents
  isLoading: boolean
  error?: Error
  onLoadMore?: (offset: number) => void
  resetExtraContents?: () => void
  hasMore: boolean
}

export const ExtraContentsContext = createContext<IExtraContentsProviderProps>(
  {} as IExtraContentsProviderProps,
)

const ERRORS_CODES = {
  EMPTY_PAGE: 'empty_page',
}

export function ExtraContentsProvider({ children }: { children: ReactNode }) {
  const [extraContentsState, setExtraContentsState] = useState<
    IExtraContents | undefined
  >()
  const [isEmptyPage, setIsEmptyPage] = useState(false)
  const isLoadingMoreRef = useRef(false)
  const requestedOffsetsRef = useRef<Set<number>>(new Set([0]))
  const toast = useToastController()
  const showApolloErrorToast = useApolloErrorToast()
  const { t } = useTranslation('toast')
  const { courseId } = useCourseRouteParams()
  const [firstLoad, setFirstLoad] = useState(true)
  const { getExtraContents, error } = useGetExtraContents({
    id: `${courseId}`,
    offset: 0,
    limit: 10,
  })

  const resetExtraContents = useCallback(() => {
    setExtraContentsState(undefined)
    setFirstLoad(true)
    isLoadingMoreRef.current = false
    requestedOffsetsRef.current = new Set([0])
  }, [])

  useEffect(() => {
    if (extraContentsState) return

    const fetchData = async () => {
      try {
        const { data } = await getExtraContents({
          variables: {
            id: courseId,
            offset: 0,
            limit: 10,
          },
        })
        setExtraContentsState(data?.course.extraContents)
        setFirstLoad(false)
      } catch (error) {
        toast.show(t('Conteúdo adicional'), {
          message: t('Erro ao carregar conteúdos adicionais'),
          type: 'error',
        })
      }
    }

    fetchData()
  }, [extraContentsState, getExtraContents, courseId, t, toast])

  useEffect(() => {
    if (
      error &&
      error.graphQLErrors[0]?.extensions?.code !== ERRORS_CODES.EMPTY_PAGE
    ) {
      showApolloErrorToast(error, 'apolloErrors.lessonExtraContents.title')
    }
  }, [error, showApolloErrorToast])

  const onLoadMore = useCallback(
    async (offset: number) => {
      if (isEmptyPage) return
      // Evita disparos concorrentes e refetch do mesmo offset. O slider dispara
      // loadMore várias vezes durante uma única animação e, como o offset vem do
      // tamanho atual de nodes (closure stale dentro da rajada), todas as chamadas
      // usariam o mesmo offset — duplicando páginas e nunca avançando até o fim.
      if (isLoadingMoreRef.current) return
      if (requestedOffsetsRef.current.has(offset)) return
      isLoadingMoreRef.current = true
      requestedOffsetsRef.current.add(offset)
      try {
        const { data, error } = await getExtraContents({
          variables: {
            id: courseId,
            offset,
            limit: 10,
          },
        })
        if (error) {
          requestedOffsetsRef.current.delete(offset)
          if (
            error.graphQLErrors[0]?.extensions?.code === ERRORS_CODES.EMPTY_PAGE
          ) {
            setIsEmptyPage(true)
            return
          }
          toast.show(t('Conteúdo adicional'), {
            message: t('Erro ao carregar conteúdos adicionais'),
            type: 'error',
          })
          return
        }
        if (data.course.extraContents?.nodes.length) {
          setExtraContentsState((prev) => {
            if (!prev) return
            const existingIds = new Set(prev.nodes.map((node) => node.id))
            const newNodes = data.course.extraContents.nodes.filter(
              (node: IExtraContent) => !existingIds.has(node.id),
            )
            if (newNodes.length === 0) return prev
            return {
              ...prev,
              nodes: [...prev.nodes, ...newNodes],
            }
          })
        }
      } finally {
        isLoadingMoreRef.current = false
      }
    },
    [isEmptyPage, getExtraContents, courseId, toast, t],
  )

  const isLoading = firstLoad || !extraContentsState

  const contextValue = useMemo(
    () => ({
      extraContents: extraContentsState ? { ...extraContentsState } : undefined,
      isLoading,
      error,
      onLoadMore: isLoading ? undefined : onLoadMore,
      resetExtraContents,
      hasMore: !isEmptyPage,
    }),
    [
      extraContentsState,
      isLoading,
      error,
      onLoadMore,
      resetExtraContents,
      isEmptyPage,
    ],
  )

  return (
    <ExtraContentsContext.Provider value={contextValue}>
      {children}
    </ExtraContentsContext.Provider>
  )
}
